import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Container,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useTitle from "hooks/useTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  BaseFilter,
  ListRequestOfBaseFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";

const ManualGradingExamsList = () => {
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("startDateTime");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { formatMessage: t } = useIntl();
  const { userEvaluationService } = useGlobalContext();
  const navigate = useNavigate();
  const { gradeExamDetailsUrl } = useGlobalContext();
  const [searchTerm, setSearchTerm] = useState("");
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);
  const { organizationId } = contextStore.useState();

  const { isError, data, error, isFetching } = useQuery(
    [
      "manualGradingExams",
      page,
      rowsPerPage,
      debouncedSearchTerm,
      organizationId,
    ],
    () =>
      userEvaluationService.getManualGradingExams(
        ListRequestOfBaseFilter.fromJS({
          sortField: orderBy,
          sortDescending: order === "desc",
          pageSize: rowsPerPage,
          pageNumber: page,
          parameters: BaseFilter.fromJS({
            searchText: debouncedSearchTerm,
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  useTitle(t({ id: "menubarGradeUserExams" }));

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      noSort: true,
      label: t({ id: "evaluationNameLabel" }),
    },
    {
      accessor: "userName",
      align: "left",
      width: 0,
      type: "text",
      noSort: true,
      label: t({ id: "userNameLabel" }),
    },
    {
      accessor: "email",
      align: "left",
      width: 0,
      type: "text",
      noSort: true,
      label: t({ id: "emailLabel" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(gradeExamDetailsUrl(id));
  };

  return (
    <Container>
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm ?? ""}
          placeholder={t({ id: "searchPlaceholder" })}
          data-cy="searchText"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),

            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setSearchTerm("")}
                  data-cy="clearSearchButton"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: data?.data ?? [],
          loading: isFetching,
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => gradeExamDetailsUrl(id)),
          ],
        }}
      />
    </Container>
  );
};

export default ManualGradingExamsList;
